import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Drawer,
  Divider,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  DialogActions,
} from "@mui/material";
import "../../App.css";
import dayjs from "dayjs";
import { TableNodata } from "../../components/TableNodata";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import { TableLoader } from "../../components/TableLoader";
import { OverAllLoader } from "../../components/OverAllLoader";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "../../providers/SnackbarProvider";
import CustomPagination from "../../components/CustomPagination";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Close } from "@mui/icons-material";
import useDebounce from "../../hooks/useDebounce";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const InstitutesList = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);
  // ==== States ========

  const tableref = useRef({});
  const location = useLocation();
  const { categoryId, viewInstitute, categoryName } = location?.state || "";
  const [openDialog, setOpenDialog] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [page, setPage] = useState(+localStorage.getItem("currentPage") || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    +localStorage.getItem("rowsPerPage") || 10
  );
  const [loading, setLoading] = useState(false);
  const [overAllLoading, setOverAllLoading] = useState(false);
  const [search, setSearch] = useState(
    localStorage.getItem("searchvalue") || ""
  );
  const debouncedSearch = useDebounce(search, 500);
  const [openDrawer, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    categoryId || +localStorage?.getItem("applyCategory") || null
  );
  console.log("selectedCategoryId", selectedCategoryId);

  const [selectedCategoryName, setSelectedCategoryName] = useState(
    localStorage?.getItem("applyCategoryName") || null
  );
  const [appliedCategory, setAppliedCategory] = useState(
    categoryId ? categoryId : +localStorage?.getItem("applyCategory") || null
  );
  const [appliedCategoryName, setAppliedCategoryName] = useState(
    categoryName
      ? categoryName
      : localStorage?.getItem("applyCategoryName") || null
  );
  const navigate = useNavigate();
  const [instituteData, setInstituteData] = useState([]);
  const [count, setCount] = useState(0);
  const [instituteNames, setInstituteNames] = useState([]);
  const [selectedInstitutes, setSelectedInstitutes] = useState([]);
  const { showSnackbar } = useSnackbar();

  // ===== Functions ===

  const handleAddInstitute = (boolean, id) => {
    navigate("/addInstitute", {
      state: {
        addInstitute: boolean,
        institute_id: id,
      },
    });
  };

  const handleSearchChange = (e) => {
    localStorage.setItem("currentPage", 0);
    setPage(0);
    const value = e.target.value;
    setSearch(value);
    localStorage.setItem("searchvalue", value);
  };

  const handleClickClear = () => {
    tableref.current = {};
    setPage(0);
    // clear component states
    setAppliedCategory(null);
    setSelectedCategoryId(null);
    setSelectedCategoryName(null);
    setAppliedCategoryName(null);

    // clear localStorage
    localStorage.removeItem("applyCategory");
    localStorage.removeItem("applyCategoryName");

    // update location.state → set category, level, testType = null
    navigate(location.pathname, {
      replace: true,
      state: {
        ...location.state,
        categoryId: null,
        categoryName: null,
      },
    });
  };

  const handleApplyCategory = () => {
    tableref.current = {};
    setPage(0);

    setAppliedCategory(selectedCategoryId);
    setAppliedCategoryName(selectedCategoryName);

    localStorage.setItem("applyCategory", selectedCategoryId);
    localStorage.setItem("applyCategoryName", selectedCategoryName);

    setOpen(false); // close drawer after applying
  };

  //====== API ======

  const instituteList = async () => {
    setLoading(true);

    const cachekey = `page_${viewInstitute ? 0 : page || 0
      }_size_${rowsPerPage}_search_${debouncedSearch}_category_id_${appliedCategory}`;

    if (tableref.current[cachekey]) {
      const cached = tableref.current[cachekey];
      setInstituteData(cached?.data);
      setCount(cached?.count);
      setLoading(false);
      setOverAllLoading(false);
      return;
    }

    try {
      const payload = {
        page: viewInstitute ? 0 : page || 0,
        size: rowsPerPage,
        search_key: debouncedSearch,
        category_id: appliedCategory || null,
      };
      const res = await api.post("ListInstitutes", payload);
      const data = res?.data?.data;
      const count = res?.data?.count;

      tableref.current[cachekey] = {
        data,
        count,
      };

      setInstituteData(res?.data?.data);
      setCount(res?.data?.count);
      setLoading(false);
      setOverAllLoading(false);
    } catch (error) {
      console.log("Error :", error);
      setLoading(false);
      setOverAllLoading(false);
      showSnackbar("error", error?.response?.data?.message || error?.message || "Failed to fetch data. Please try again!");
    }
  };

  const listCategory = async () => {
    try {
      const res = await api.post("CategoryListForInstituteAssignment", {
        institute_id: 0,
      });
      if (res.data.status === "success") {
        setCategories(res.data.data); // store categories array
      }
    } catch (error) {
      console.log("Error fetching categories:", error);
    }
  };

  const instituteDropDown = async () => {
    try {
      const payload = {
        category_id: categoryId,
      };
      const { data } = await api.post("ListActiveInstitutes", payload);
      setInstituteNames(data?.data ?? []);
    } catch (error) {
      console.error("Error fetching institutes:", error);
    }
  };

  const handleAssign = async () => {
    setOverAllLoading(true);

    try {
      const res = await api.post("AssignInstituteToCategory", {
        institute_ids: selectedInstitutes,
        category_ids: [selectedCategoryId],
      });
      if (res?.data?.status === "success") {
        tableref.current = {};
        showSnackbar("success", res?.data?.message);
        setOpenDialog(false);
        instituteList();
      } else {
        showSnackbar("error", res?.data?.message);
      }
    } catch (error) {
      console.log("Error :", error);
      showSnackbar("error", error?.response?.data?.message || error?.message || "Failed to assign categories. Please try again!");
    } finally {
      setOverAllLoading(false);
    }
  };

  // ===== UseEffects ===

  useEffect(() => {
    if (!overAllLoading) {
      instituteList();
    }
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    setOverAllLoading(true);
    instituteList();
    listCategory();
    if (viewInstitute) {
      instituteDropDown();
    }
  }, []);

  useEffect(() => {
    instituteList();
  }, [appliedCategory]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTable(true);
    }, 200); // 0.2 sec delay

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    localStorage.setItem("currentPage", page);
    localStorage.setItem("rowsPerPage", rowsPerPage);
  }, [page, rowsPerPage]);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            height: "100vh",
            borderRadius: "12px 0 0 12px",
          },
        }}
      >
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight={600} color="primary">
              Filters
            </Typography>
          </Box>
          <IconButton aria-label="Close" size="small" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Trainer */}
        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            Subject
          </Typography>
          {/* Trainer Filter */}
          {localStorage.getItem("role") !== "TRAINER" && (
            <Autocomplete
              size="small"
              sx={{ minWidth: 220 }}
              options={categories}
              value={categories?.find(
                (category) => category?.category_id === selectedCategoryId
              )}
              getOptionLabel={(option) => option?.category_name || ""}
              onChange={(event, newValue) => {
                const categoryId = newValue ? newValue.category_id : "";
                setSelectedCategoryId(categoryId);
                setSelectedCategoryName(newValue?.category_name);
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Subject"
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
            />
          )}
        </Box>

        {/* Apply Button */}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="customButton"
          onClick={handleApplyCategory}
        >
          Apply Filters
        </Button>
      </Drawer>
      {viewInstitute === true && (
        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          {/* Back Button */}
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "6px",
              cursor: "pointer",
              width: "fit-content",
              transition: "0.2s",
              "&:hover": { color: "#0a73b1" },
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.history.back();
              }
            }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIcon sx={{ fontSize: "18px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "inherit" }}
            >
              Back
            </Typography>
          </Grid>
        </Grid>
      )}

      {overAllLoading ? (
        <OverAllLoader message="Please wait..." />
      ) : (
        <Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            sx={{
              // borderBottom: "1px solid #828487",
              pb: 2,
            }}
          >
            <Grid
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 0.5,
                alignItems: "center",
              }}
            >
              <Box className="icon-box">
                <BusinessIcon sx={{ color: "#1976d2", fontSize: 28 }} />
              </Box>
              <Typography
                sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}
              >
                {getLabel("INSTITUTE")}
              </Typography>
            </Grid>
            <Box display="flex" gap={4} alignItems="center">
              <TextField
                value={search}
                size="small"
                variant="outlined"
                placeholder={`Search ${getLabel("INSTITUTE")}`}
                onChange={handleSearchChange}
                className="custom-search-field"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {viewInstitute ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  className="gradient-button"
                  onClick={() => (
                    setOpenDialog(true), setSelectedInstitutes([])
                  )}
                >
                  Assign {getLabel("INSTITUTE")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  className="gradient-button"
                  onClick={() => handleAddInstitute("true", null)}
                >
                  Add  {getLabel("INSTITUTE")}
                </Button>
              )}
            </Box>
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              opacity: showTable ? 1 : 0,
              transform: showTable ? "translateY(0)" : "translateY(40px)",
              transition: "opacity 0.5s ease, transform 0.5s ease",
            }}
          >
            <Table>
              {/* size="small" reduces padding */}
              <TableHead>
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {appliedCategoryName &&
                          appliedCategoryName !== "undefined" && (
                            <Box className="filter-chip">
                              <Typography
                                sx={{ fontSize: "13px", fontWeight: 500 }}
                              >
                                {localStorage?.getItem("applyCategoryName") ||
                                  appliedCategoryName}
                              </Typography>
                              {!viewInstitute && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setAppliedCategory(null);
                                    setAppliedCategoryName(null);
                                    setSelectedCategoryId(null);
                                    setPage(0);
                                    localStorage.removeItem("applyCategory");
                                    localStorage.removeItem(
                                      "applyCategoryName"
                                    );
                                    navigate(location.pathname, {
                                      replace: true,
                                      state: {
                                        ...location.state,
                                        categoryId: null,
                                        categoryName: null,
                                      },
                                    });
                                  }}
                                  sx={{ ml: 0.5 }}
                                >
                                  <CloseIcon sx={{ fontSize: "14px" }} />
                                </IconButton>
                              )}
                            </Box>
                          )}
                      </Box>
                      {!viewInstitute && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            onClick={() => setOpen(true)}
                            color="primary"
                            className="more-filter-btn"
                          >
                            <FilterListIcon />
                            <Typography className="more-filter-text">
                              More filter
                            </Typography>
                          </IconButton>

                          {appliedCategory !== null && (
                            <Button
                              variant="outlined"
                              className="clear-filter-btn"
                              onClick={handleClickClear}
                            >
                              Clear Filter
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="tableHeader">
                    <b>S.No</b>
                  </TableCell>
                  <TableCell className="tableHeader">
                    <b>{getLabel("INSTITUTE")} Name</b>
                  </TableCell>
                  <TableCell className="tableHeader">
                    <b>Address</b>
                  </TableCell>
                  <TableCell className="tableHeader">
                    <b>Contact Number</b>
                  </TableCell>
                  <TableCell className="tableHeader">
                    <b>Email</b>
                  </TableCell>
                  {/* <TableCell sx={{ width: "120px", whiteSpace: "nowrap" }}>
                    <b>Created By</b>
                  </TableCell> */}
                  <TableCell className="tableHeader">
                    <b>Created Date</b>
                  </TableCell>
                  <TableCell className="tableHeader">
                    <b>Status</b>
                  </TableCell>
                  <TableCell align="center" className="tableHeader">
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              {loading ? (
                <TableCell colSpan={8} align="center">
                  <TableLoader message={`Fetching ${getLabel("INSTITUTE")}...`} />
                </TableCell>
              ) : (
                <TableBody className="tablebody">
                  {instituteData.map((item, index) => (
                    <TableRow
                      key={item.assessment_id}
                      hover
                      sx={{
                        backgroundColor:
                          index % 2 === 1 ? "#FAFAFA" : "inherit",
                        "&:hover": { backgroundColor: "#f1f1f1" },
                      }}
                    >
                      <TableCell align="center">
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        {item.city}
                        <br />
                        {item?.state}
                        <br />
                        {item?.pincode}
                      </TableCell>
                      <TableCell>{item.contact_phone}</TableCell>
                      <TableCell>{item.contact_email}</TableCell>
                      {/* <TableCell>{item.created_by}</TableCell> */}
                      <TableCell>
                        {item?.created_at
                          ? dayjs(item.created_at).format("DD-MM-YYYY")
                          : "-"}
                      </TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1.5,
                            py: 0.3,
                            borderRadius: "12px",
                            fontWeight: "bold",
                            fontSize: "12px",
                            backgroundColor:
                              item.status === "ACTIVE"
                                ? "#d4edda"
                                : item.status === "INACTIVE"
                                  ? "#f8d7da"
                                  : "#f1f1f1",
                            color:
                              item.status === "ACTIVE"
                                ? "#155724"
                                : item.status === "INACTIVE"
                                  ? "#721c24"
                                  : "#333",
                            textAlign: "center",
                          }}
                        >
                          {item.status === "ACTIVE"
                            ? "Active"
                            : item.status === "INACTIVE"
                              ? "Inactive"
                              : "-"}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            localStorage.getItem("role") === "SUPER_ADMIN"
                              ? "Assign Subject & View details"
                              : "View"
                          }
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "primary.main",
                                color: "white",
                                fontSize: 13,
                                fontWeight: 500,
                              },
                            },
                            arrow: {
                              sx: {
                                color: "primary.main",
                              },
                            },
                          }}
                        >
                          <IconButton
                            color="primary"
                            // onClick={() =>
                            //   handleAddInstitute("false", item?.institute_id)
                            // }
                            onClick={() =>
                              navigate("/viewInstitute", {
                                state: {
                                  institute_id: item?.institute_id,
                                },
                              })
                            }
                          >
                            <VisibilityIcon />
                            <Typography
                              color="primary"
                              sx={{ fontSize: "12px", ml: "4px" }}
                            >
                              View
                            </Typography>
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {((page === 0 && instituteData?.length > rowsPerPage - 1) ||
                    page !== 0) && (
                      <CustomPagination
                        page={page}
                        setPage={setPage}
                        rowsPerPage={rowsPerPage}
                        count={count}
                        colSpan={8}
                      />
                    )}

                  {instituteData?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <TableNodata />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog
        open={openDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 12px 36px rgba(0,0,0,0.12)",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "linear-gradient(90deg, #f5f9ff, #eef4fb)",
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 2,
            px: 3,
          }}
        >
          <Box display="flex" alignItems="center">
            <AddCircleOutlineIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight={600}>
              Assign {getLabel("INSTITUTE")}
            </Typography>
          </Box>

          <IconButton onClick={() => setOpenDialog(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              margin: "10px 0px",
            }}
          >
            <Typography sx={{ fontWeight: "bold" }}>{`Select ${getLabel("INSTITUTE")}`}</Typography>
          </Box>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={instituteNames}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) =>
              option.institute_id === value.institute_id
            }
            value={instituteNames.filter((inst) =>
              selectedInstitutes.includes(inst.institute_id)
            )}
            onChange={(event, newValue) =>
              setSelectedInstitutes(
                newValue ? newValue.map((item) => item.institute_id) : []
              )
            }
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: "8px 14px" }}>
                <Checkbox checked={selected} />
                <Typography fontWeight={500}>{option.name}</Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder={`Select ${getLabel("INSTITUTE")}(s)...`} />
            )}
          />
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "grey.50",
          }}
        >
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={selectedInstitutes.length === 0 || overAllLoading}
          >
            Assign {getLabel("INSTITUTE")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstitutesList;
